import { useState } from "react";
import { ChevronDown, ChevronRight, Target } from "lucide-react";

const FuelEfficiency = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const performanceMetrics = [
    {
      id: "totalMTDay",
      title: "Total MT/Day",
      description: "Total fuel consumption in metric tons per day",
      calculation: "Aggregated daily fuel consumption from VesselFuelParameter records",
      calculationDetails: [
        "Data Source: VesselFuelParameter collection",
        "Target: Typically 20.2 MT/day (configurable per vessel)",
        "Units: Metric tons per day (MT/day)",
      ],
      targetExample: "20.2 MT/day",
      borderColor: "border-red-200",
      bgColor: "bg-red-50",
      textColor: "text-red-700",
    },
    {
      id: "vesselFuelParameters",
      title: "Vessel Fuel Parameters Structure",
      description: "Data format stored in VesselFuelParameter collection",
      calculation: "Each record contains date, parameters object, and operational data",
      calculationDetails: [
        "recordDate: Date of measurement",
        "parameters: Object containing metrics like 'Total MT/Day'",
        "voyageId: Link to specific voyage (if voyage-based)"
      ],
      targetExample: "Structured time series data",
      borderColor: "border-green-200",
      bgColor: "bg-green-50",
      textColor: "text-green-700",
    },
    {
      id: "targetManagement",
      title: "Target Management",
      description: "How fuel efficiency targets are stored and applied",
      calculation: "Targets stored in vessel.fuelEfficiencyTargets field",
      calculationDetails: [
        "Storage: Targets stored per vessel in Vessel collection",
        "Default: 20.2 MT/day if no vessel targets specified",
        "Comparison: Daily averages compared against target values",
      ],
      targetExample: "Configurable per-vessel targets",
      borderColor: "border-pink-200",
      bgColor: "bg-pink-50",
      textColor: "text-pink-700",
    },
  ];

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#e9cbf5] rounded-lg">
              <Target className="w-6 h-6 text-[#7d3c98]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                Vessel Fuel Efficiency Analysis
              </h2>
              <p className="text-gray-600 text-sm">
                Daily fuel parameter aggregation and target-based performance tracking
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200">

            <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded">
              <p className="text-sm text-orange-800">
                <strong>Primary Metric:</strong> The system currently focuses on <strong>"Total MT/Day"</strong> as the main fuel efficiency metric, calculated as daily time series with statistical ranges (average, min, max) and compared against vessel-specific targets.
              </p>
            </div>
            
            {/* Performance Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {performanceMetrics.map((metric) => (
                <div
                  key={metric.id}
                  className={`p-5 rounded-lg border ${metric.borderColor} ${metric.bgColor}`}
                >
                  <h4 className="font-semibold text-gray-800 mb-3">
                    {metric.title}
                  </h4>
                  
                  <div className="mb-3">
                    <p className="text-sm font-medium text-gray-700 mb-1">Description:</p>
                    <p className={`text-sm ${metric.textColor}`}>
                      {metric.description}
                    </p>
                  </div>

                  <div className="mb-3">
                    <p className="text-sm font-medium text-gray-700 mb-1">Calculation Method:</p>
                    <div className="p-2 bg-white rounded border border-gray-200">
                      <p className={`font-mono text-sm ${metric.textColor}`}>
                        {metric.calculation}
                      </p>
                    </div>
                  </div>

                  {metric.calculationDetails && (
                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-1">Implementation Details:</p>
                      <ul className="text-xs text-gray-600 space-y-1">
                        {metric.calculationDetails.map((detail, index) => (
                          <li key={index}>• {detail}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-gray-500">Target/Example:</span>
                      <span className={`text-xs font-semibold ${metric.textColor}`}>
                        {metric.targetExample}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FuelEfficiency;